import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { localized } from '@monrdv/shared';
import { ApiError, request } from '../lib/api.js';

/**
 * Hiring somebody, from the screen that manages people.
 *
 * Until now the only way to add a colleague was the `auth grant` command on the server, which
 * is the right tool for the very first account and the wrong one for the fourteenth.
 *
 * **The email is optional, and the form says why.** 13 of the 31 imported people have no work
 * address: a helper whose hours are logged and whose name is on a visit, but who never opens
 * the app. Requiring an address would mean somebody invents one, and an invented address is one
 * a real message can eventually reach. Left empty, the API mints the same `@no-login.invalid`
 * placeholder the import used, and the confirmation says which of the two happened — because
 * "can this person sign in" is the difference the person filling this in actually cares about.
 */

/** Declared locally, as `Team.tsx` does: the shared type lives in the server's db package. */
type Translatable = { fr: string; en: string };

export function AddTeamMember({
  roles,
  canSetRate,
}: {
  roles: Array<{ id: string; key: string; name: Translatable; landing: 'portal' | 'field' }>;
  canSetRate: boolean;
}) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [roleId, setRoleId] = useState('');
  const [locale, setLocale] = useState<'fr' | 'en'>('fr');
  const [hourlyRate, setHourlyRate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<{ email: string; canSignIn: boolean } | null>(null);

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setHourlyRate('');
    setError(null);
  };

  const create = useMutation({
    mutationFn: () =>
      request<{ userId: string; email: string; canSignIn: boolean }>('/team', {
        method: 'POST',
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          roleId,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          locale,
          hourlyRate: canSetRate && hourlyRate.trim() ? hourlyRate.trim() : undefined,
        }),
      }),
    onSuccess: (result) => {
      setAdded({ email: result.email, canSignIn: result.canSignIn });
      reset();
      void client.invalidateQueries({ queryKey: ['team'] });
      // A new colleague appears in every assignee list, so the cached options are now short.
      void client.invalidateQueries({ queryKey: ['jobFilterOptions'] });
      void client.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (err) =>
      setError(err instanceof ApiError && err.message ? err.message : 'La personne n’a pas pu être ajoutée.'),
  });

  if (!open) {
    return (
      <div className="recform__row">
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            setAdded(null);
            setOpen(true);
            if (!roleId && roles.length) setRoleId(roles[0]!.id);
          }}
        >
          Ajouter une personne
        </button>
        <span className="recform__spacer" />
      </div>
    );
  }

  const ready = !!firstName.trim() && !!lastName.trim() && !!roleId;
  const busy = create.isPending;

  return (
    <form
      className="recform__section"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        setAdded(null);
        if (ready) create.mutate();
      }}
      noValidate
    >
      <legend className="recform__legend">Ajouter une personne</legend>

      {error && (
        <p className="recform__error" role="alert">
          {error}
        </p>
      )}

      {/* Says plainly whether they can get in, because that is the consequence of the field
          left empty above and nobody should have to infer it from an address. */}
      {added && (
        <p className="recform__notice" role="status">
          {added.canSignIn
            ? `Ajouté. ${added.email} peut se connecter — envoyez-lui un lien depuis sa fiche.`
            : `Ajouté sans accès à l’application (${added.email}). Ses heures et ses visites sont attribuables ; il ne peut pas se connecter.`}
        </p>
      )}

      <div className="recform__pair">
        <div className="recform__field">
          <label className="recform__label" htmlFor="tm-first">
            Prénom
          </label>
          <input
            id="tm-first"
            className="recform__input"
            value={firstName}
            disabled={busy}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="recform__field">
          <label className="recform__label" htmlFor="tm-last">
            Nom
          </label>
          <input
            id="tm-last"
            className="recform__input"
            value={lastName}
            disabled={busy}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>

      <div className="recform__pair">
        <div className="recform__field">
          <label className="recform__label" htmlFor="tm-role">
            Rôle
          </label>
          <select
            id="tm-role"
            className="recform__select"
            value={roleId}
            disabled={busy}
            onChange={(e) => setRoleId(e.target.value)}
          >
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {localized(role.name, 'fr')}
              </option>
            ))}
          </select>
          {/* The role decides which application they land in, which is worth saying here
              rather than leaving them to discover it at their first sign-in. */}
          <span className="recform__hint">
            {roles.find((r) => r.id === roleId)?.landing === 'field'
              ? 'Ouvre l’application terrain.'
              : 'Ouvre le portail de gestion.'}
          </span>
        </div>
        <div className="recform__field">
          <label className="recform__label" htmlFor="tm-locale">
            Langue
          </label>
          <select
            id="tm-locale"
            className="recform__select"
            value={locale}
            disabled={busy}
            onChange={(e) => setLocale(e.target.value as 'fr' | 'en')}
          >
            <option value="fr">Français</option>
            <option value="en">Anglais</option>
          </select>
        </div>
      </div>

      <div className="recform__pair">
        <div className="recform__field">
          <label className="recform__label" htmlFor="tm-email">
            Courriel <span className="recform__optional">facultatif</span>
          </label>
          <input
            id="tm-email"
            className="recform__input"
            type="email"
            value={email}
            disabled={busy}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
          />
          <span className="recform__hint">
            Laissez vide pour quelqu’un qui n’utilisera pas l’application. Ses heures et ses visites restent
            attribuables.
          </span>
        </div>
        <div className="recform__field">
          <label className="recform__label" htmlFor="tm-phone">
            Téléphone <span className="recform__optional">facultatif</span>
          </label>
          <input
            id="tm-phone"
            className="recform__input recform__input--tel"
            inputMode="tel"
            value={phone}
            disabled={busy}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>

      {/* Only for somebody allowed to see what people are paid; the API checks it again. */}
      {canSetRate && (
        <div className="recform__field">
          <label className="recform__label" htmlFor="tm-rate">
            Taux horaire <span className="recform__optional">facultatif</span>
          </label>
          <input
            id="tm-rate"
            className="recform__input tnum"
            inputMode="decimal"
            placeholder="28.50"
            value={hourlyRate}
            disabled={busy}
            onChange={(e) => setHourlyRate(e.target.value)}
          />
        </div>
      )}

      <div className="recform__row">
        <button className="btn btn--primary" type="submit" disabled={busy || !ready}>
          {busy ? 'Ajout…' : 'Ajouter'}
        </button>
        <button
          className="btn btn--ghost"
          type="button"
          disabled={busy}
          onClick={() => {
            setOpen(false);
            reset();
          }}
        >
          Fermer
        </button>
        <span className="recform__spacer" />
      </div>
    </form>
  );
}
